import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useApi, apiResolver } from "@/api/useApi";

import {
  AuiConfiguration,
  UpsertAuiConfigurationDto,
} from "./AuiConfiguration.model";

const QUERY_KEY = "aui-configurations";

export function useAuiConfigurationApi() {
  const { api } = useApi();

  return useMemo(
    () => ({
      getConfigurations: async () =>
        apiResolver<AuiConfiguration[]>(
          api.get(`/sessions-api/aui/configurations`)
        ),
      upsertConfiguration: async (
        id: string,
        payload: UpsertAuiConfigurationDto
      ) =>
        apiResolver<AuiConfiguration>(
          api.post(`/sessions-api/aui/configurations/${id}`, payload)
        ),
      deleteConfiguration: async (id: string) =>
        apiResolver<void>(api.delete(`/sessions-api/aui/configurations/${id}`)),
    }),
    [api]
  );
}

export function useAuiConfigurations(enabled = true) {
  const { getConfigurations } = useAuiConfigurationApi();

  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: getConfigurations,
    enabled,
  });
}

export function useDeleteAuiConfiguration() {
  const { deleteConfiguration } = useAuiConfigurationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteConfiguration(id),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY]);
    },
  });
}

export function useUpsertAuiConfiguration() {
  const { upsertConfiguration } = useAuiConfigurationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpsertAuiConfigurationDto;
    }) => upsertConfiguration(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY]);
    },
  });
}
